import queryClient from '@src/app/providers/queryClient';
import type {
  Alert,
  AlertCreate,
  AlertDto,
  AlertReassign,
  AlertReassignDto,
  AlertResolve,
  AlertResolveDto,
  AlertStatusChoicesResponse,
  AlertSummaryResponse,
  AlertTypeSummary,
  AlertTypeSummaryDto,
  AlertUpdate,
  AlertUpdateDto,
  AlertsResponse,
} from '@src/features/alerts';
import {
  alertCreateToFormData,
  alertFromDto,
  alertUpdateToDto,
} from '@src/features/alerts/lib';
import useAPIClient from '@src/shared/hooks/api/useAPIClient';
import { useMutation, useQuery } from '@src/shared/query';
import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
} from '@tanstack/react-query';

import { alertsUrl } from './constants';

const ALERT_SUMMARY_REFETCH_INTERVAL = 60 * 1000;

export type UseAlertsParams = {
  filterParams?: Record<string, unknown>;
  pageSize: number;
  ordering?: string;
  retainedAlertIds?: number[];
};

export type AlertsListResponse = Omit<AlertsResponse, 'results'> & {
  results: Alert[];
};

export type UpdateAlertPayload = {
  id: number | string;
  payload: Partial<AlertUpdate>;
};

type AlertListQueryKey = [
  'root',
  'alerts',
  number,
  string | undefined,
  Record<string, unknown>,
];

function getAlertListQueryKey({
  filterParams = {},
  ordering,
  pageSize,
}: UseAlertsParams): AlertListQueryKey {
  return ['root', 'alerts', pageSize, ordering, filterParams];
}

function isAlertsInfiniteData(
  data: unknown,
): data is InfiniteData<AlertsResponse> {
  return (
    data !== null &&
    data !== undefined &&
    typeof data === 'object' &&
    'pages' in data &&
    Array.isArray((data as InfiniteData<AlertsResponse>).pages)
  );
}

function updateAlertInCachedLists(updatedAlertDto: AlertDto) {
  queryClient.setQueriesData(
    { queryKey: ['root', 'alerts'], exact: false },
    (oldData: unknown) => {
      if (!isAlertsInfiniteData(oldData)) {
        return oldData;
      }

      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          results: page.results.map((alert) =>
            alert.id === updatedAlertDto.id ? updatedAlertDto : alert,
          ),
        })),
      };
    },
  );
}

export function markAlertListsStale() {
  return queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === 'root' &&
      query.queryKey[1] === 'alerts' &&
      typeof query.queryKey[2] === 'number',
    refetchType: 'none',
  });
}

export function prependAlertToCachedList(
  params: Pick<UseAlertsParams, 'filterParams' | 'ordering' | 'pageSize'>,
  createdAlertDto: AlertDto,
) {
  queryClient.setQueryData<InfiniteData<AlertsResponse>>(
    getAlertListQueryKey(params),
    (oldData: InfiniteData<AlertsResponse> | undefined) => {
      if (!oldData) {
        return oldData;
      }

      return {
        ...oldData,
        pages: oldData.pages.map((page, index) => {
          const results = page.results.filter(
            (alert) => alert.id !== createdAlertDto.id,
          );
          return index === 0
            ? {
                ...page,
                results: [createdAlertDto, ...results],
              }
            : {
                ...page,
                results,
              };
        }),
      };
    },
  );
}

function invalidateAlertSummary() {
  return queryClient.invalidateQueries({
    queryKey: ['root', 'alerts', 'summary'],
  });
}

function handleAlertMutationError() {
  void markAlertListsStale();
  void invalidateAlertSummary();
}

export function useAlerts({
  filterParams = {},
  pageSize,
  ordering,
  retainedAlertIds = [],
}: UseAlertsParams) {
  const apiClient = useAPIClient();

  return useInfiniteQuery({
    queryKey: getAlertListQueryKey({ filterParams, ordering, pageSize }),
    initialPageParam: 1,
    placeholderData: keepPreviousData,
    queryFn: ({ pageParam, signal }) =>
      apiClient.request({
        url: alertsUrl,
        payload: {
          page: String(pageParam),
          page_size: String(pageSize),
          ...filterParams,
          ...(retainedAlertIds.length > 0
            ? { retained_ids: retainedAlertIds.join(',') }
            : {}),
          ...(ordering ? { ordering } : {}),
        },
        options: { signal },
      }) as Promise<AlertsResponse>,
    select: (data): InfiniteData<AlertsListResponse> => {
      const seenAlertIds = new Set<number>();

      return {
        ...data,
        pages: (data.pages ?? []).map((page) => ({
          ...page,
          results: page.results
            .map(alertFromDto)
            .filter((alert) => {
              if (seenAlertIds.has(alert.id)) {
                return false;
              }
              seenAlertIds.add(alert.id);
              return true;
            }),
        })),
      };
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.meta.next) {
        return undefined;
      }
      return lastPage.meta.page + 1;
    },
  });
}

export function useAlertStatusChoices() {
  return useQuery<AlertStatusChoicesResponse>(
    'alerts/choices/alert-statuses/',
    {
      initialData: {
        opened: 'Opened',
        resolved: 'Resolved',
      },
    },
  );
}

export function useAlertTypes() {
  return useQuery<{ results: AlertTypeSummaryDto[] }, AlertTypeSummary[]>(
    'alerts/types/',
    {
      initialData: { results: [] },
      queryParams: {
        page_size: '500',
      },
      select: (data) =>
        data.results.map((alertType) => ({
          id: alertType.id,
          name: alertType.name,
          icon: alertType.icon,
          sort: alertType.alty_sort,
        })),
    },
  );
}

export function useAlertSummary() {
  return useQuery<AlertSummaryResponse>('alerts/summary/', {
    initialData: {
      own_open_alerts: 0,
      deputed_open_alerts: 0,
      managed_open_alerts: 0,
    },
    refetchInterval: ALERT_SUMMARY_REFETCH_INTERVAL,
  });
}

export function useUpdateAlert() {
  return useMutation<AlertDto, Error, UpdateAlertPayload>({
    mutationKey: ['root', 'alerts', 'update'],
    mutateFn: ({ apiClient, id, payload }) =>
      apiClient.request({
        url: `${alertsUrl}${id}/`,
        method: 'PATCH',
        payload: alertUpdateToDto(payload) as Partial<AlertUpdateDto>,
      }) as Promise<AlertDto>,
    onSuccess: (updatedAlertDto) => {
      updateAlertInCachedLists(updatedAlertDto);
    },
    onError: handleAlertMutationError,
  });
}

export function useCreateAlert() {
  return useMutation<AlertDto, Error, AlertCreate>({
    mutationKey: ['root', 'alerts', 'create'],
    mutateFn: ({ apiClient, ...payload }) =>
      apiClient.request({
        url: alertsUrl,
        method: 'POST',
        payload: alertCreateToFormData(payload),
        contentType: 'multipart/form-data',
      }) as Promise<AlertDto>,
    onSuccess: () => {
      void invalidateAlertSummary();
    },
    onError: handleAlertMutationError,
  });
}

export function useResolveAlert() {
  return useMutation<AlertDto, Error, AlertResolve>({
    mutationKey: ['root', 'alerts', 'resolve'],
    mutateFn: ({ apiClient, id, resolutionNotes }) => {
      const payload: AlertResolveDto = {};
      if (resolutionNotes !== undefined) {
        payload.aler_notes = resolutionNotes;
      }
      return apiClient.request({
        url: `${alertsUrl}${id}/resolve/`,
        method: 'POST',
        payload,
      }) as Promise<AlertDto>;
    },
    onSuccess: (updatedAlertDto) => {
      updateAlertInCachedLists(updatedAlertDto);
      void invalidateAlertSummary();
    },
    onError: handleAlertMutationError,
  });
}

export function useReassignAlert() {
  return useMutation<AlertDto, Error, AlertReassign>({
    mutationKey: ['root', 'alerts', 'reassign'],
    mutateFn: ({ apiClient, id, userId }) =>
      apiClient.request({
        url: `${alertsUrl}${id}/reassign/`,
        method: 'POST',
        payload: {
          aler_user_pic: userId,
        } satisfies AlertReassignDto,
      }) as Promise<AlertDto>,
    onSuccess: (updatedAlertDto) => {
      updateAlertInCachedLists(updatedAlertDto);
      void invalidateAlertSummary();
    },
    onError: handleAlertMutationError,
  });
}
